import { useMemo, useState } from "react";
import { Reveal } from "./Reveal";
import { SectionTag } from "./SectionTag";

type Scenario = "pessimiste" | "realiste" | "optimiste";

const scenarioRates: Record<Scenario, number> = {
  pessimiste: 5,
  realiste: 10,
  optimiste: 18,
};

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (v: number) => void;
}

function SliderRow({ label, value, min, max, step = 1, suffix = "", onChange }: SliderRowProps) {
  return (
    <div>
      <p className="text-sm font-medium text-ink-soft">{label}</p>
      <p className="mt-1 text-3xl font-extrabold text-ink">
        {value}
        {suffix}
      </p>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-line accent-[#ff8452]"
      />
    </div>
  );
}

export function RoiCalculator() {
  const [clientsPerDay, setClientsPerDay] = useState(20);
  const [openDays, setOpenDays] = useState(26);
  const [avgBasket, setAvgBasket] = useState(18);
  const [scenario, setScenario] = useState<Scenario>("realiste");

  const additionalRevenue = useMemo(() => {
    const annualGross = clientsPerDay * openDays * avgBasket * 12;
    const base = annualGross * (scenarioRates[scenario] / 100);
    return Math.round(base);
  }, [clientsPerDay, openDays, avgBasket, scenario]);

  return (
    <section className="bg-cream py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <SectionTag>Calculateur</SectionTag>
          <h2 className="mt-5 text-4xl font-extrabold tracking-tight text-ink lg:text-5xl">
            Calculez votre manque à gagner
          </h2>
          <p className="mt-4 text-lg text-ink-soft">
            Déplacez les curseurs. Voyez combien la fidélisation représente pour vous.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 grid gap-6 rounded-2xl border border-line bg-white p-8 shadow-card lg:grid-cols-2">
            <div className="flex flex-col gap-8">
              <SliderRow
                label="Clients par jour"
                value={clientsPerDay}
                min={5}
                max={150}
                onChange={setClientsPerDay}
              />
              <SliderRow
                label="Jours ouverts / mois"
                value={openDays}
                min={10}
                max={31}
                onChange={setOpenDays}
              />
              <SliderRow
                label="Panier moyen (€)"
                value={avgBasket}
                min={5}
                max={300}
                suffix="€"
                onChange={setAvgBasket}
              />
            </div>

            <div className="flex flex-col rounded-2xl bg-ink p-7 text-white">
              <div className="flex gap-2 rounded-full bg-white/10 p-1">
                {(["pessimiste", "realiste", "optimiste"] as Scenario[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => setScenario(s)}
                    className={`flex-1 rounded-full py-2 text-xs font-semibold capitalize transition-colors ${
                      scenario === s ? "bg-white text-ink" : "text-white/60"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <p className="mt-6 text-sm text-white/60">
                Si seulement {scenarioRates[scenario]}% de vos clients reviennent grâce à
                la carte
              </p>

              <p className="mt-1 text-sm text-white/60">Ventes annuelles supplémentaires</p>
              <p className="mt-1 text-5xl font-extrabold">
                {additionalRevenue.toLocaleString("fr-FR")} €
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  "Plus de visites répétées",
                  "Meilleure rétention client",
                  "Panier moyen en hausse",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full bg-mint/15 px-3 py-1.5 text-xs font-semibold text-mint"
                  >
                    {chip}
                  </span>
                ))}
              </div>

              <a
                href="#demo"
                className="mt-6 rounded-full bg-gradient-brand py-3.5 text-center text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
              >
                Je veux une démo gratuite →
              </a>
              <p className="mt-3 text-center text-[11px] text-white/50">
                Estimation basée sur vos chiffres — à ajuster selon votre activité.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
